import { useState, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { useIsMobile } from '@/hooks/use-mobile';

interface CareCircleMember {
  id: string;
  name: string;
  email: string;
  relationship: string;
  contactFrequency?: string;
  accessLevel: string;
  interests?: string[];
  createdDate?: string;
}

interface MemberNode {
  member: CareCircleMember;
  interest: string;
  frequency: string;
  angle: number;
  radius: number;
  x: number;
  y: number;
}

interface CareCircleRadarProps {
  members: CareCircleMember[];
}

const FREQUENCY_TIERS = ['Hourly', 'Daily', 'Weekly', 'Monthly', 'Quarterly', 'Annually', 'Occasionally'];

const INTEREST_COLORS: Record<string, string> = {
  Physical: '#ef4444',
  Mental: '#f59e0b',
  Emotional: '#ec4899',
  Spiritual: '#8b5cf6',
  Social: '#3b82f6',
  Financial: '#10b981',
  Occupational: '#06b6d4'
};

// Generate a consistent color for any interest (predefined or custom)
const getInterestColor = (interest: string): string => {
  // Return predefined color if it exists
  if (INTEREST_COLORS[interest]) {
    return INTEREST_COLORS[interest];
  }

  // Generate a consistent color based on the interest name
  let hash = 0;
  for (let i = 0; i < interest.length; i++) {
    hash = interest.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Generate HSL color with good saturation and lightness for visibility
  const hue = Math.abs(hash) % 360;
  const saturation = 65 + (Math.abs(hash) % 15); // 65-80%
  const lightness = 45 + (Math.abs(hash) % 10); // 45-55%

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

export const CareCircleRadar = ({ members }: CareCircleRadarProps) => {
  const [hoveredNode, setHoveredNode] = useState<MemberNode | null>(null);
  const isMobile = useIsMobile();

  // SVG dimensions - responsive based on device
  const width = isMobile ? 360 : 800;
  const height = isMobile ? 360 : 800;
  const centerX = width / 2;
  const centerY = height / 2;
  const maxRadius = Math.min(width, height) / 2 - (isMobile ? 40 : 80);
  const minRadius = isMobile ? 20 : 40;

  // Process data to create nodes
  const { nodes, segmentData } = useMemo(() => {
    console.log('📊 CareCircleRadar Members:', JSON.stringify(members, null, 2));

    const nodesArray: MemberNode[] = [];
    const interestCounts: Record<string, number> = {};
    const MIN_NODE_DISTANCE = 15; // Minimum distance between nodes in pixels
    const MAX_PLACEMENT_ATTEMPTS = 20;

    // Helper function to check if a position is too close to existing nodes
    const isTooClose = (x: number, y: number, existingNodes: MemberNode[]): boolean => {
      return existingNodes.some(node => {
        const distance = Math.sqrt(Math.pow(x - node.x, 2) + Math.pow(y - node.y, 2));
        return distance < MIN_NODE_DISTANCE;
      });
    };

    // Count members per interest
    members.forEach(member => {
      if (member.interests && member.interests.length > 0) {
        member.interests.forEach(interest => {
          interestCounts[interest] = (interestCounts[interest] || 0) + 1;
        });
      }
    });

    // Calculate total for proportional sizing
    const totalCount = Object.values(interestCounts).reduce((sum, count) => sum + count, 0);

    // Create segment data with angles
    // Sort interests with "Physical" first, then alphabetically
    const interests = Object.keys(interestCounts).sort((a, b) => {
      if (a === 'Physical') return -1;
      if (b === 'Physical') return 1;
      return a.localeCompare(b);
    });
    let currentAngle = -Math.PI / 2; // Start from top (12 o'clock)
    const segments = interests.map(interest => {
      const count = interestCounts[interest];
      const proportion = totalCount > 0 ? count / totalCount : 1 / interests.length;
      const angleSize = proportion * 2 * Math.PI;
      const segment = {
        interest,
        count,
        startAngle: currentAngle,
        endAngle: currentAngle + angleSize,
        midAngle: currentAngle + angleSize / 2
      };
      currentAngle += angleSize;
      return segment;
    });

    // Create nodes for each member-interest combination
    members.forEach(member => {
      if (!member.interests || member.interests.length === 0) return;

      member.interests.forEach(interest => {
        const segment = segments.find(s => s.interest === interest);
        if (!segment) return;

        // Determine radius based on frequency
        const frequency = member.contactFrequency || 'Occasionally';
        const frequencyIndex = FREQUENCY_TIERS.indexOf(frequency);
        const validFrequencyIndex = frequencyIndex >= 0 ? frequencyIndex : FREQUENCY_TIERS.length - 1;

        // Map frequency to radius (closer to center = more frequent)
        const radiusStep = (maxRadius - minRadius) / FREQUENCY_TIERS.length;
        const radius = minRadius + radiusStep * (validFrequencyIndex + 0.5);

        // Try to find a position that doesn't overlap with existing nodes
        let x: number = 0;
        let y: number = 0;
        let angle: number = 0;
        let foundValidPosition = false;

        for (let attempt = 0; attempt < MAX_PLACEMENT_ATTEMPTS; attempt++) {
          // Calculate angle within segment (add some randomness to avoid overlap)
          const angleRange = segment.endAngle - segment.startAngle;
          const randomOffset = (Math.random() - 0.5) * angleRange * 0.8;
          angle = segment.midAngle + randomOffset;

          // Try slight radius variations if needed
          const radiusVariation = attempt > 10 ? (Math.random() - 0.5) * radiusStep * 0.3 : 0;
          const adjustedRadius = Math.max(minRadius, Math.min(maxRadius, radius + radiusVariation));

          // Convert polar to cartesian
          x = centerX + adjustedRadius * Math.cos(angle);
          y = centerY + adjustedRadius * Math.sin(angle);

          // Check if this position is valid
          if (!isTooClose(x, y, nodesArray)) {
            foundValidPosition = true;
            break;
          }
        }

        // If we couldn't find a valid position after all attempts, use the last calculated position anyway
        // (better to have some overlap than to exclude nodes entirely)

        nodesArray.push({
          member,
          interest,
          frequency,
          angle,
          radius,
          x,
          y
        });
      });
    });

    return { nodes: nodesArray, segmentData: segments };
  }, [members, maxRadius, minRadius, centerX, centerY, isMobile]);

  // Generate frequency rings
  const rings = FREQUENCY_TIERS.map((tier, index) => {
    const radiusStep = (maxRadius - minRadius) / FREQUENCY_TIERS.length;
    const radius = minRadius + radiusStep * (index + 1);
    return { tier, radius };
  });

  // Create SVG path for segment
  const createSegmentPath = (startAngle: number, endAngle: number, innerRadius: number, outerRadius: number) => {
    const x1 = centerX + innerRadius * Math.cos(startAngle);
    const y1 = centerY + innerRadius * Math.sin(startAngle);
    const x2 = centerX + outerRadius * Math.cos(startAngle);
    const y2 = centerY + outerRadius * Math.sin(startAngle);
    const x3 = centerX + outerRadius * Math.cos(endAngle);
    const y3 = centerY + outerRadius * Math.sin(endAngle);
    const x4 = centerX + innerRadius * Math.cos(endAngle);
    const y4 = centerY + innerRadius * Math.sin(endAngle);

    const largeArcFlag = endAngle - startAngle > Math.PI ? 1 : 0;

    return `
      M ${x1} ${y1}
      L ${x2} ${y2}
      A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x3} ${y3}
      L ${x4} ${y4}
      A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x1} ${y1}
      Z
    `;
  };

  return (
    <Card className="p-3 md:p-6">
      <div className="space-y-2 md:space-y-4">
        {/* Legend - hidden on mobile, shows all interests including custom ones */}
        {!isMobile && segmentData.length > 0 && (
          <div className="flex flex-wrap gap-2 md:gap-4 justify-center text-xs md:text-sm">
            {segmentData.map(segment => (
              <div key={segment.interest} className="flex items-center gap-1 md:gap-2">
                <div
                  className="w-3 h-3 md:w-4 md:h-4 rounded"
                  style={{ backgroundColor: getInterestColor(segment.interest) }}
                />
                <span className="text-muted-foreground">{segment.interest}</span>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-center overflow-x-auto">
          <svg width={width} height={height} className="border rounded-lg bg-white" style={{ minWidth: isMobile ? '360px' : 'auto' }}>
            {/* Frequency rings */}
            <g className="rings">
              {rings.map((ring, index) => (
                <g key={ring.tier}>
                  <circle
                    cx={centerX}
                    cy={centerY}
                    r={ring.radius}
                    fill="none"
                    stroke="#e5e7eb"
                    strokeWidth="1"
                  />
                  {/* Ring label */}
                  <text
                    x={centerX}
                    y={centerY + ring.radius - 5}
                    textAnchor="middle"
                    fontSize={isMobile ? "7" : "10"}
                    fill="#9ca3af"
                  >
                    {ring.tier}
                  </text>
                </g>
              ))}
            </g>

            {/* Interest segments */}
            <g className="segments">
              {segmentData.map(segment => (
                <g key={segment.interest}>
                  <path
                    d={createSegmentPath(segment.startAngle, segment.endAngle, minRadius, maxRadius)}
                    fill={getInterestColor(segment.interest)}
                    opacity="0.1"
                    stroke={getInterestColor(segment.interest)}
                    strokeWidth="1"
                  />
                  {/* Segment label */}
                  <text
                    x={centerX + (maxRadius + (isMobile ? 15 : 30)) * Math.cos(segment.midAngle)}
                    y={centerY + (maxRadius + (isMobile ? 15 : 30)) * Math.sin(segment.midAngle) - (isMobile ? 5 : 8)}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={isMobile ? "7" : "12"}
                    fontWeight="600"
                    fill={getInterestColor(segment.interest)}
                  >
                    {isMobile ? segment.interest.substring(0, 4) : segment.interest}
                  </text>
                  <text
                    x={centerX + (maxRadius + (isMobile ? 15 : 30)) * Math.cos(segment.midAngle)}
                    y={centerY + (maxRadius + (isMobile ? 15 : 30)) * Math.sin(segment.midAngle) + (isMobile ? 5 : 8)}
                    x={centerX + (maxRadius + (isMobile ? 15 : 30)) * Math.cos(segment.midAngle)}
                    y={centerY + (maxRadius + (isMobile ? 15 : 30)) * Math.sin(segment.midAngle) + (isMobile ? 5 : 8)}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={isMobile ? "7" : "10"}
                    fill="#6b7280"
                  >
                    ({segment.count})
                  </text>
                </g>
              ))}
            </g>

            {/* Center point (you) */}
            <g className="center">
              <circle
                cx={centerX}
                cy={centerY}
                r={isMobile ? "8" : "12"}
                fill="#3b82f6"
                stroke="white"
                strokeWidth={isMobile ? "2" : "3"}
              />
              <text
                x={centerX}
                y={centerY + (isMobile ? 18 : 25)}
                textAnchor="middle"
                fontSize={isMobile ? "9" : "12"}
                fontWeight="600"
                fill="#1f2937"
              >
                You
              </text>
            </g>

            {/* Member nodes */}
            <g className="nodes">
              {nodes.map((node, index) => (
                <g
                  key={`${node.member.id}-${node.interest}-${index}`}
                  onMouseEnter={() => !isMobile && setHoveredNode(node)}
                  onMouseLeave={() => !isMobile && setHoveredNode(null)}
                  onClick={() => isMobile && setHoveredNode(hoveredNode === node ? null : node)}
                  onTouchStart={() => isMobile && setHoveredNode(node)}
                  className="cursor-pointer"
                >
                  <circle
                    cx={node.x}
                    cy={node.y}
                    r={hoveredNode === node ? (isMobile ? 6 : 8) : (isMobile ? 4 : 6)}
                    fill={getInterestColor(node.interest)}
                    stroke="white"
                    strokeWidth={isMobile ? "1" : "2"}
                    opacity={hoveredNode && hoveredNode !== node ? 0.4 : 1}
                    className="transition-all"
                  />
                </g>
              ))}
            </g>

            {/* Tooltip */}
            {hoveredNode && (
              <g className="tooltip">
                <rect
                  x={hoveredNode.x + (isMobile ? 8 : 15)}
                  y={hoveredNode.y - (isMobile ? 30 : 40)}
                  width={isMobile ? "140" : "180"}
                  height={isMobile ? "70" : "85"}
                  fill="white"
                  stroke="#d1d5db"
                  strokeWidth="1"
                  rx="4"
                  filter="url(#shadow)"
                />
                <text
                  x={hoveredNode.x + (isMobile ? 13 : 25)}
                  y={hoveredNode.y - (isMobile ? 15 : 20)}
                  fontSize={isMobile ? "9" : "12"}
                  fontWeight="600"
                  fill="#1f2937"
                >
                  {isMobile && hoveredNode.member.name.length > 15
                    ? hoveredNode.member.name.substring(0, 15) + '...'
                    : hoveredNode.member.name}
                </text>
                <text
                  x={hoveredNode.x + (isMobile ? 13 : 25)}
                  y={hoveredNode.y - (isMobile ? 3 : 5)}
                  fontSize={isMobile ? "7" : "10"}
                  fill="#6b7280"
                >
                  {hoveredNode.member.relationship}
                </text>
                <text
                  x={hoveredNode.x + (isMobile ? 13 : 25)}
                  y={hoveredNode.y + (isMobile ? 8 : 10)}
                  fontSize={isMobile ? "7" : "10"}
                  fill="#6b7280"
                >
                  {hoveredNode.interest}
                </text>
                <text
                  x={hoveredNode.x + (isMobile ? 13 : 25)}
                  y={hoveredNode.y + (isMobile ? 19 : 25)}
                  fontSize={isMobile ? "7" : "10"}
                  fill="#6b7280"
                >
                  {hoveredNode.frequency}
                </text>
                {!isMobile && (
                  <text
                    x={hoveredNode.x + 25}
                    y={hoveredNode.y + 40}
                    fontSize="10"
                    fill="#6b7280"
                  >
                    Added: {hoveredNode.member.createdDate
                      ? new Date(hoveredNode.member.createdDate).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })
                      : 'Unknown'}
                  </text>
                )}
              </g>
            )}

            {/* Shadow filter for tooltip */}
            <defs>
              <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="2" stdDeviation="4" floodOpacity="0.1"/>
              </filter>
            </defs>
          </svg>
        </div>
      </div>
    </Card>
  );
};
